import { ShaderMaterial, Vector3, Web3DViewer } from '@technology/web3d';
import { ThreeJsManager } from '@trimble/web3d-utils';
import { calculateDistanceBasedScale } from '../distance-based-scale-calculation';
import {
  createStationPointVisual,
  type StationData,
  type StationPoint,
  type StationVisualConfig,
} from './station-visual-creator';

interface StationMarkerUniforms {
  innerOpacity: { value: number };
  outerOpacity: { value: number };
  globalOpacity: { value: number };
  arrowAngle: { value: number };
  hasArrow: { value: number };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Required for ShaderMaterial compatibility
  [uniform: string]: { value: any };
}

interface StationMarkerMaterial extends ShaderMaterial {
  uniforms: StationMarkerUniforms;
}

const GLOBAL_OPACITY_MAX = 1.0;
const OPACITY_ANIMATION_SPEED = 0.08;
// Hovered marker tweens faster so the user gets snappier feedback.
const OPACITY_ANIMATION_SPEED_HOVER = 0.2;
const SCALE_ANIMATION_SPEED = 0.05;
const SCALE_ANIMATION_THRESHOLD = 0.5;
const ANIMATION_TOLERANCE = 0.01;
const BATCH_SIZE = 30;

export interface StationMarkerPipelineConfig {
  readonly stationPointSize: number;
  readonly stationNearBoundary: number;
  readonly stationFarBoundary: number;
  readonly lodMarkerDistanceThreshold: number;
  readonly hoverScaleMultiplier: number;
  readonly hoverScaleMultiplierFar: number;
}

/**
 * Read-only view of shared station metadata + clip state. The pipeline
 * never owns or mutates the station map structure — only its bookkeeping
 * fields during the heavy update.
 */
export interface StationMarkerPipelineDeps {
  getAllStationData(): Iterable<StationData>;
  getStationDataByIndex(index: number): StationData | undefined;
  getStationDataByKey(key: string): StationData | undefined;
  /** Overridden by 2D map for a fixed Z. */
  getStationZ(pos: Vector3): number;
  isStationMarkersVisible(): boolean;
  isPointInsideActiveClip(position: Vector3): boolean;
}

/**
 * @intent Owns every marker mesh and every animation that touches one
 * (LOD create/destroy, hover, scale + opacity tweens, disposal). The view
 * manager owns the underlying `StationData` and pushes pick/hover events
 * here through the public API — the pipeline never subscribes to the
 * viewer itself.
 */
export class StationMarkerPipeline {
  private readonly viewer: Web3DViewer;
  private readonly threeJsManager: ThreeJsManager;
  private readonly config: StationMarkerPipelineConfig;
  private readonly visualConfig: StationVisualConfig;
  private readonly deps: StationMarkerPipelineDeps;

  /** Active marker meshes only — stations within LOD threshold or hovered. */
  private points: Array<StationPoint> = [];
  private readonly markerByPositionKey: Map<string, StationPoint> = new Map();

  private currentHoverPoint: StationPoint | null = null;

  /**
   * Driven by capability `isActive` via {@link setUnhoveredOpacity}. Used
   * both as the creation default and as the restore target on hover release.
   */
  private unhoveredOpacity: number = GLOBAL_OPACITY_MAX;

  /** Read by the manager to decide whether path target colours need recomputing. */
  private lodStateChanged = false;

  constructor(
    viewer: Web3DViewer,
    threeJsManager: ThreeJsManager,
    config: StationMarkerPipelineConfig,
    visualConfig: StationVisualConfig,
    deps: StationMarkerPipelineDeps
  ) {
    this.viewer = viewer;
    this.threeJsManager = threeJsManager;
    this.config = config;
    this.visualConfig = visualConfig;
    this.deps = deps;
  }

  public get markerPoints(): readonly StationPoint[] {
    return this.points;
  }

  public get hoveredPoint(): StationPoint | null {
    return this.currentHoverPoint;
  }

  public get hadLodTransition(): boolean {
    return this.lodStateChanged;
  }

  public get currentUnhoveredOpacity(): number {
    return this.unhoveredOpacity;
  }

  /** Returns true when the value actually changed. */
  public setUnhoveredOpacity(value: number): boolean {
    if (this.unhoveredOpacity === value) {
      return false;
    }
    this.unhoveredOpacity = value;
    for (const point of this.points) {
      point.targetOpacity = value;
    }
    return true;
  }

  /** Hovered marker keeps its full opacity. */
  public dimNonHoveredMarkers(value: number): void {
    for (const point of this.points) {
      if (point === this.currentHoverPoint) {
        continue;
      }
      point.targetOpacity = value;
    }
  }

  public restoreAllMarkersOpacity(value: number): void {
    for (const point of this.points) {
      point.targetOpacity = value;
    }
  }

  /**
   * Heavy-update pass: distance-based LOD create/destroy plus scale-target
   * updates. The manager throttles invocation; this just does the work.
   */
  public runHeavyUpdate(markersVisible: boolean): boolean {
    const needsRefresh = this.updateLODLevels(markersVisible);

    if (markersVisible) {
      for (const point of this.points) {
        if (point.isWithinMarkerDistance || point === this.currentHoverPoint) {
          this.updateScale(point);
        }
      }
    } else if (this.currentHoverPoint) {
      // Hover keeps a marker visible when markers are toggled off; mirror
      // the LOD flag and recompute scale so zoom doesn't freeze its size.
      const hoverData = this.deps.getStationDataByKey(
        StationMarkerPipeline.positionKey(
          this.currentHoverPoint.originalPosition
        )
      );
      if (hoverData) {
        this.currentHoverPoint.isWithinMarkerDistance =
          hoverData.isWithinMarkerDistance;
      }
      this.updateScale(this.currentHoverPoint);
    }

    return needsRefresh;
  }

  /**
   * Distance-based LOD with clip-volume gating. `isWithinMarkerDistance`
   * stays purely distance-based; clipped stations get their mesh disposed
   * but keep their bookkeeping so they re-create cleanly on un-clip.
   */
  private updateLODLevels(markersVisible: boolean): boolean {
    const cameraPos = this.viewer.camera.position;
    const lodThreshold = this.config.lodMarkerDistanceThreshold;
    let needsRefresh = false;
    this.lodStateChanged = false;

    for (const data of this.deps.getAllStationData()) {
      const distance = cameraPos.distanceTo(data.position);
      const isWithinDistance = distance < lodThreshold;
      const isInsideClip = this.deps.isPointInsideActiveClip(data.position);
      const wasWithinDistance = data.isWithinMarkerDistance;
      const isTransitioning = wasWithinDistance !== isWithinDistance;

      if (isTransitioning) {
        this.lodStateChanged = true;
      }

      data.isWithinMarkerDistance = isWithinDistance;
      data.distance = distance;

      const existingMarker = this.markerByPositionKey.get(data.key);

      if (isWithinDistance && markersVisible && isInsideClip) {
        if (!existingMarker) {
          const point = this.createStationPoint(
            data.originalPosition,
            data.stationIndex,
            this.unhoveredOpacity,
            data.directionAngle,
            data.hasArrow
          );
          point.isWithinMarkerDistance = true;
          point.targetOpacity = this.unhoveredOpacity;
          point.mesh.visible = true;
          this.threeJsManager.addObject(point.mesh);
          this.markerByPositionKey.set(data.key, point);
          this.points.push(point);
          needsRefresh = true;
        } else if (existingMarker !== this.currentHoverPoint) {
          existingMarker.isWithinMarkerDistance = true;
          existingMarker.targetOpacity = this.unhoveredOpacity;
          existingMarker.mesh.raycast = existingMarker.originalRaycast;
          if (!existingMarker.mesh.visible) {
            existingMarker.mesh.visible = true;
            const material = existingMarker.mesh
              .material as StationMarkerMaterial;
            material.uniforms.globalOpacity.value = this.unhoveredOpacity;
            needsRefresh = true;
          }
        } else if (!existingMarker.mesh.visible) {
          // Hovered marker re-entered the clip volume — restore visibility
          // while preserving the hover state set by handleHover.
          existingMarker.mesh.visible = true;
          needsRefresh = true;
        }
      } else if (!isWithinDistance || !isInsideClip) {
        if (existingMarker && existingMarker !== this.currentHoverPoint) {
          this.disposeMarker(existingMarker);
          needsRefresh = true;
        } else if (existingMarker) {
          // Hovered marker survives a distance-only LOD transition (so the
          // user's interaction survives a zoom-out), but a clip transition
          // must hide it — leaving it visible would render it floating
          // outside the active clip volume.
          existingMarker.isWithinMarkerDistance = false;
          if (!isInsideClip && existingMarker.mesh.visible) {
            existingMarker.mesh.visible = false;
            needsRefresh = true;
          }
        }
      }
    }

    return needsRefresh;
  }

  /**
   * Near-zone scaling is intentionally off (`undefined` nearBoundary):
   * `baseSize * distance / nearBoundary` collapses toward zero on close
   * approach, making the marker vanish on zoom-through and pop back on
   * the far side. Holding base size in the near/comfort zone removes
   * those jerky resizes.
   */
  private updateScale(point: StationPoint): void {
    const distance = this.viewer.camera.position.distanceTo(point.position);

    const newBaseScale = calculateDistanceBasedScale(
      this.viewer.camera.position,
      point.position,
      this.config.stationPointSize,
      undefined,
      this.config.stationFarBoundary
    );

    point.baseScale = newBaseScale;
    if (point === this.currentHoverPoint) {
      const hoverMultiplier = point.isWithinMarkerDistance
        ? this.config.hoverScaleMultiplier
        : this.config.hoverScaleMultiplierFar;
      point.targetScale = newBaseScale * hoverMultiplier;
    } else {
      point.targetScale = newBaseScale;
    }

    point.distance = distance;
  }

  /**
   * Runs every frame regardless of `markersVisible` so fade-outs complete
   * cleanly when markers are toggled off.
   */
  public runLightAnimations(markersVisible: boolean): boolean {
    let needsRefresh = false;
    for (const point of this.points) {
      const material = point.mesh.material as StationMarkerMaterial;
      const currentOpacity = material.uniforms.globalOpacity.value;
      const currentScale = point.mesh.scale.x;

      const isAnimating =
        Math.abs(currentOpacity - point.targetOpacity) > ANIMATION_TOLERANCE ||
        Math.abs(currentScale - point.targetScale) > ANIMATION_TOLERANCE;

      if (isAnimating) {
        // Hover should keep animating even when markers are toggled off.
        if (markersVisible || point === this.currentHoverPoint) {
          needsRefresh = this.animatePointScale(point) || needsRefresh;
        }
        needsRefresh = this.animatePointOpacity(point) || needsRefresh;
      }
    }
    return needsRefresh;
  }

  private animatePointScale(point: StationPoint): boolean {
    const currentScale = point.mesh.scale.x;
    const targetScale = point.targetScale;
    const scaleDiff = Math.abs(targetScale - currentScale);

    if (scaleDiff <= ANIMATION_TOLERANCE) {
      return false;
    }

    const newScale =
      scaleDiff > SCALE_ANIMATION_THRESHOLD
        ? targetScale
        : currentScale +
          Math.sign(targetScale - currentScale) *
            Math.min(SCALE_ANIMATION_SPEED, scaleDiff);

    point.mesh.scale.setScalar(newScale);
    point.mesh.updateMatrixWorld(true);
    return true;
  }

  private animatePointOpacity(point: StationPoint): boolean {
    const material = point.mesh.material as StationMarkerMaterial;
    const currentOpacity = material.uniforms.globalOpacity.value;
    const targetOpacity = point.targetOpacity;

    if (Math.abs(currentOpacity - targetOpacity) <= ANIMATION_TOLERANCE) {
      // Hide a fully-faded mesh so renderer can skip it.
      if (currentOpacity <= ANIMATION_TOLERANCE && point.mesh.visible) {
        point.mesh.visible = false;
        return true;
      }
      return false;
    }

    const animationSpeed =
      point === this.currentHoverPoint
        ? OPACITY_ANIMATION_SPEED_HOVER
        : OPACITY_ANIMATION_SPEED;

    const newOpacity =
      currentOpacity +
      Math.sign(targetOpacity - currentOpacity) *
        Math.min(animationSpeed, Math.abs(targetOpacity - currentOpacity));

    material.uniforms.globalOpacity.value = newOpacity;
    return true;
  }

  /**
   * Fades via opacity tween. On show, the LOD pass restores the opacity
   * target on the next heavy update; on hide, fading to 0 lets
   * `animatePointOpacity` clear `mesh.visible` for free frames.
   */
  public applyVisibility(visible: boolean): void {
    this.points.forEach((point) => {
      if (visible) {
        if (!point.mesh.visible) {
          point.mesh.visible = true;
        }
      } else {
        point.targetOpacity = 0;
      }
    });
    this.threeJsManager.forceRefresh();
  }

  /** Drops the reference without any visual reset (for wholesale teardown). */
  public clearHover(): void {
    this.currentHoverPoint = null;
  }

  /**
   * Far stations (or any station while markers are hidden) get their
   * temporary hover-marker disposed; near stations with markers visible
   * are restored to the unhovered baseline.
   */
  public resetHover(): void {
    if (!this.currentHoverPoint) {
      return;
    }

    const point = this.currentHoverPoint;
    this.currentHoverPoint = null;

    if (!point.isWithinMarkerDistance || !this.deps.isStationMarkersVisible()) {
      this.disposeMarker(point);
    } else {
      point.targetScale = point.baseScale;
      point.targetOpacity = this.unhoveredOpacity;
    }
  }

  /** Caller must {@link resetHover} first to tear down any previous hover. */
  public setHover(point: StationPoint): void {
    this.currentHoverPoint = point;

    point.mesh.visible = true;
    point.mesh.raycast = point.originalRaycast;
    this.updateScale(point);

    const hoverMultiplier = point.isWithinMarkerDistance
      ? this.config.hoverScaleMultiplier
      : this.config.hoverScaleMultiplierFar;
    point.targetScale = point.baseScale * hoverMultiplier;
    point.targetOpacity = GLOBAL_OPACITY_MAX;
  }

  public findByMeshId(meshId: number): StationPoint | undefined {
    return this.points.find((p) => p.mesh.id === meshId);
  }

  /**
   * Resolves the station closest to the camera among the two endpoints
   * of a path segment, creating a temporary marker for a far station that
   * has no active marker (path-hover entry point).
   */
  public getOrCreateClosestStationForSegment(
    startIndex: number,
    endIndex: number
  ): StationPoint | null {
    const startData = this.deps.getStationDataByIndex(startIndex);
    const endData = this.deps.getStationDataByIndex(endIndex);

    if (!startData && !endData) {
      return null;
    }

    let chosenData: StationData;
    if (!startData) {
      chosenData = endData!;
    } else if (!endData) {
      chosenData = startData;
    } else {
      const cameraPos = this.viewer.camera.position;
      chosenData =
        cameraPos.distanceTo(startData.position) <
        cameraPos.distanceTo(endData.position)
          ? startData
          : endData;
    }

    const existing = this.markerByPositionKey.get(chosenData.key);
    if (existing) {
      return existing;
    }

    const point = this.createStationPoint(
      chosenData.originalPosition,
      chosenData.stationIndex,
      0,
      chosenData.directionAngle,
      chosenData.hasArrow
    );
    point.isWithinMarkerDistance = false;
    point.targetOpacity = GLOBAL_OPACITY_MAX;
    this.threeJsManager.addObject(point.mesh);
    this.markerByPositionKey.set(chosenData.key, point);
    this.points.push(point);
    return point;
  }

  /** Disposal runs in batches off the main thread (see disposeMarkersInBackground). */
  public disposeMarkersForKeys(removedKeys: string[]): void {
    const removedSet = new Set(removedKeys);

    const markersToRemove = this.points.filter((point) =>
      removedSet.has(StationMarkerPipeline.positionKey(point.originalPosition))
    );
    if (markersToRemove.length > 0) {
      void this.disposeMarkersInBackground(markersToRemove);
    }

    for (const key of removedKeys) {
      this.markerByPositionKey.delete(key);
    }
    this.points = this.points.filter(
      (point) =>
        !removedSet.has(
          StationMarkerPipeline.positionKey(point.originalPosition)
        )
    );

    if (this.currentHoverPoint) {
      const hoverKey = StationMarkerPipeline.positionKey(
        this.currentHoverPoint.originalPosition
      );
      if (removedSet.has(hoverKey)) {
        this.currentHoverPoint = null;
      }
    }
  }

  /** Resyncs `stationIndex` after a path rebuild has reordered stationsInfo. */
  public reindexMarkers(): void {
    for (const point of this.points) {
      const key = StationMarkerPipeline.positionKey(point.originalPosition);
      const data = this.deps.getStationDataByKey(key);
      if (data) {
        point.stationIndex = data.stationIndex;
      }
    }
  }

  /** Bookkeeping clears synchronously before any await so rebuilds don't race. */
  public async disposeAll(): Promise<void> {
    const pointsToRemove = this.points;

    this.points = [];
    this.markerByPositionKey.clear();
    this.currentHoverPoint = null;

    await this.disposeMarkersInBackground(pointsToRemove);
  }

  public static positionKey(pos: Vector3): string {
    return `${pos.x}_${pos.y}_${pos.z}`;
  }

  private createStationPoint(
    stationPosition: Vector3,
    index: number,
    initialOpacity: number,
    directionAngle: number = 0,
    hasArrow: boolean = false
  ): StationPoint {
    return createStationPointVisual(
      stationPosition,
      index,
      initialOpacity,
      this.deps.getStationZ(stationPosition),
      this.viewer.camera.position,
      this.visualConfig,
      directionAngle,
      hasArrow
    );
  }

  private disposeMarker(point: StationPoint): void {
    this.threeJsManager.removeObject(point.mesh);
    point.mesh.geometry.dispose();
    if (Array.isArray(point.mesh.material)) {
      point.mesh.material.forEach((m) => m.dispose());
    } else {
      point.mesh.material.dispose();
    }
    const key = StationMarkerPipeline.positionKey(point.originalPosition);
    this.markerByPositionKey.delete(key);
    const idx = this.points.indexOf(point);
    if (idx !== -1) {
      this.points.splice(idx, 1);
    }
  }

  /**
   * Yields to the browser between batches via `requestAnimationFrame` so
   * disposing large station sets doesn't blow the frame budget.
   */
  private async disposeMarkersInBackground(
    points: StationPoint[]
  ): Promise<void> {
    try {
      for (let i = 0; i < points.length; i += BATCH_SIZE) {
        const batch = points.slice(i, i + BATCH_SIZE);

        const meshes = batch.map((p) => p.mesh);
        await this.threeJsManager.removeObject(...meshes);

        batch.forEach((point) => {
          point.mesh.geometry.dispose();

          if (Array.isArray(point.mesh.material)) {
            point.mesh.material.forEach((mat) => {
              mat.dispose();
            });
          } else {
            point.mesh.material.dispose();
          }
        });

        if (i + BATCH_SIZE < points.length) {
          await this.waitForNextFrame();
        }
      }
    } catch (error) {
      console.error('[StationsNav] Error disposing markers:', error);
    }
  }

  private async waitForNextFrame(): Promise<void> {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  }
}
